import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  Formik,
  Form,
  Field,
  FieldArray,
  ErrorMessage,
} from 'formik';
import * as yup from 'yup';
import cx from 'classnames';
import moment from 'moment-timezone';
import { toNumber } from '../../utils/numbers';

import Button from 'react-bootstrap/lib/Button';
import Alert from 'react-bootstrap/lib/Alert';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import FormControl from 'react-bootstrap/lib/FormControl';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import Panel from 'react-bootstrap/lib/Panel';
import DateTimePicker from 'react-datetime';

import './Schedule.css';
import shortid from 'shortid';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import SideBar from '../../components/SideBar/SideBar';
import { Checkbox } from 'react-bootstrap';
import { Dropdown } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { AccountType } from '../../constants/ItemType';
import { getLocaleName } from '../../utils/date';
import { OrderOrigin } from '../../constants/OrderOrigin';

class OrderForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
    };

    this.schema = null;
  }

  componentDidMount() {
    this.schema = this.validationSchema;
  }

  get validationSchema() {
    const { configuration } = this.props.settingsStore;
    const maxDropSize = configuration ? configuration.maximumDropSize || 10 : 10;

    const ProductSchema = yup.object().shape({
      itemId: yup.string().required('Required!'),
      quantity: yup.number().min(1, 'Minimum: 1').test('Drop Size', `Max drop size: ${maxDropSize}`, function(value) {
        if (!this.options.parent.isMix) {
          return true;
        }
        return this.options.parent.isMix && value <= maxDropSize;
      }),
    });

    return yup.object().shape({
      date: yup.date().required('Required!'),
      products: yup.array().of(ProductSchema),
    });
  }

  getSubTotal = (values) => {
    let subTotal = 0;
    if (!values || !values.products) {
      return subTotal;
    }
    values.products.forEach(p => {
      subTotal += Number(p.quantity || 0) * Number(p.unitPrice || 0);
    });

    return subTotal;
  };

  getTax = (values) => {
    let taxAmount = 0;
    let amount = 0;
    if (!values || !values.products) {
      return taxAmount;
    }

    values.products.forEach(p => {
      if ((p.isTaxable || values.origin === OrderOrigin.External) && Number(values.taxRate)) {
        amount = Number(p.quantity || 0) * Number(p.unitPrice || 0);
        taxAmount += (amount * Number(values.taxRate || 0)) / 100;
      }
    });

    return taxAmount;
  };

  onCustomerChange = (customerId, values, setValues) => {
    const { scheduleStore } = this.props;
    const customer = scheduleStore.customers.find(v => v.id === Number(customerId)) || {};

    setValues({
      ...values,
      customerId,
      customerName: customer.name,
      customerAddress: `${customer.name}
${customer.address || ''}`,
    });
  };

  getItem = (itemId, isMix) => {
    const { allMixes, allExtra, mixes, extra } = this.props.mixStore;
    return isMix
      ? allMixes.find(v => v.itemId === Number(itemId)) || mixes.find(v => v.itemId === Number(itemId)) || {}
      : allExtra.find(v => v.itemId === Number(itemId)) || extra.find(v => v.itemId === Number(itemId)) || {};
  };

  onTruckChange = (truckId, values, setValues) => {
    const { scheduleStore } = this.props;
    const truck = scheduleStore.trucks.find(v => v.id === Number(truckId)) || {};
    const driver = truck.driverId ? scheduleStore.drivers.find(v => v.id === truck.driverId) || {} : {};
    const payload = {
      ...values,
      truckId,
      truckCode: truck.code,
      driverId: driver.id,
      driverName: driver.name,
      driverCode: driver.code,
    };

    setValues(payload);
  };

  onDriverChange = (driverId, values, setValues) => {
    const { scheduleStore } = this.props;
    const driver = scheduleStore.drivers.find(v => v.id === Number(driverId)) || {};
    const payload = {
      ...values,
      driverId,
      driverName: driver.name,
      driverCode: driver.code,
    };

    setValues(payload);
  };

  onProductChange(itemId, product, index, values, setValues) {
    const item = this.getItem(itemId, product.isMix) || {};
    let payload = { ...values, mixSlump: product.isMix ? item.slump : '', mixSlumpString: product.isMix ? item.slumpString : '' };

    payload.products[index] = {
      ...payload.products[index],
      itemId: item.itemId,
      itemCode: item.itemCode,
      itemDescription: item.itemDescription,
      unitId: item.unitId,
      unitSymbol: item.unitSymbol,
      unitCode: item.unitCode,
      isTaxable: item.taxable,
    };

    setValues(payload);
  }

  onResetForm = resetForm => {
    const { scheduleStore, sidebarStore } = this.props;
    resetForm({
      values: this.getInitialValues(),
    });
    scheduleStore.orderId = null;

    sidebarStore.onToggle('schedules');

    this.setState({
      error: null,
    });
  };

  getInitialValues() {
    const { timezone } = this.props.scheduleStore;
    const { configuration } = this.props.settingsStore;

    return {
      id: shortid.generate(),
      date: moment().tz(timezone),
      origin: OrderOrigin.Local,
      printWithoutWeights: configuration ? configuration.printWithoutWeights : false,
      printWithWeights: configuration ? configuration.printWithWeights : true,
      printOnlyWeights: configuration ? configuration.printOnlyWeights : false,
      products: [{
        isMix: true,
        quantity: 0,
        itemId: '',
      }],
    };
  }

  // TODO: Remove this once confirmed backend correctly calculates tax quantity
  patchInvalidTaxQuantity = (order) => {
    // Order can return taxQuantity: 0, even when each product
    // has taxQuantity > 0. This should be a backend fix (classic), but it's faster
    // to patch it on the frontend.
    return {
      totalQuantityPrice: order.totalQuantityPrice || toNumber(order.products.reduce((total, product) => total + product.totalQuantityPrice, 0), 2),
      totalQuantityTax: order.totalQuantityTax || toNumber(order.products.reduce((total, product) => total + product.totalQuantityTax, 0), 2),
    };
  };

  getDataFromOrder(orderId) {
    const { scheduleStore } = this.props;
    const order = scheduleStore.getScheduleById(orderId) || scheduleStore.getLocalOrderById(orderId);
    return {
      ...order,
      ...this.patchInvalidTaxQuantity(order),
    };
  }

  render() {
    const { scheduleStore, sidebarStore, mixStore, batchStore, newAuthStore, t } = this.props;

    const {
      timezone,
      trucks,
      drivers,
      customers,
      orderId,
      actionMode,
      accountType,
      isPreview,
      isAdd,
      isEdit,
    } = scheduleStore;
    const { user } = newAuthStore;

    const { mixOptions, extraOptions } = mixStore;
    const driverOptions = mixStore.getOptions(drivers, {
      key: 'id',
      text: ['code', 'name'],
      value: 'id',
      subheader: 'code',
      description: 'name',
    });
    const truckOptions = mixStore.getOptions(trucks, {
      key: 'id',
      text: ['code', 'description'],
      value: ['id'],
      subheader: 'code',
      description: 'description',
    });
    const customerOptions = mixStore.getOptions(customers, {
      key: 'id',
      text: ['code', 'name'],
      value: ['id'],
      subheader: 'code',
      description: 'name',
    });

    const { canEmit } = batchStore;

    return !canEmit ? '' : (
      <SideBar type="orderForm" title={`${actionMode} Order ${orderId ? orderId : ''}`} icon={'fa-file-text-o'}>
        <Formik
          enableReinitialize
          initialValues={
            orderId
              ? this.getDataFromOrder(orderId)
              : this.getInitialValues()
          }
          validateOnChange={true}
          validationSchema={this.schema}
          onSubmit={(values, actions) => {
            this.setState({
              error: null,
            });

            actions.setSubmitting(true);

            let promise;
            const mix = values.products.find(v => v.isMix);
            const data = {
              ...values,
              date: moment(values.date).tz(timezone).format(),
              mixSlump: mix.slump,
              mixSlumpString: mix.slumpString,
              taxRate: values.taxRate/100,
              orderDeliveredMixQuantity: values.orderDeliveredMixQuantity,
            };

            if (isAdd) {
              promise = scheduleStore.addLocalOrder(data);
            } else if (isEdit) {
              promise = scheduleStore.updateLocalOrder(orderId, data);
            } else {
              actions.setSubmitting(false);
              return;
            }

            promise
              .then(() => {
                sidebarStore.onToggle('schedules');
                actions.setValues(this.getInitialValues());
              })
              .catch((e) => {
                this.setState({
                  error: `API error: ${e ? e.message : ' Can not save'}`,
                });
                actions.setValues(values);
                actions.setSubmitting(false);
              });
          }
          }
        >{({ handleSubmit,
            handleChange,
            setFieldValue,
            setValues,
            values,
            errors,
            touched,
            resetForm,
            isSubmitting }) => (
            <Form className={`schedule-form action-${actionMode}`}>
              <Row>
                <Col sm={12}>
                  <Row className={'no-borders'}>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('Order Code')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.code}</div>
                          : (
                            <Field name="code"
                              value={values.code || ''}
                              disabled={isPreview}
                              className="form-control input-sm" />
                          )}
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup className={cx({'has-error':  errors.date && touched.date })}>
                        <ControlLabel>{t('Ship Date')}</ControlLabel>
                        {isPreview ? (
                          <Field name="date"
                            value={values.date || ''}
                            disabled={isPreview}
                            className="form-control input-sm"/>
                        )
                          : (
                            <DateTimePicker
                              name="date"
                              type="text"
                              inputProps={{ className: 'form-control input-sm' }}
                              onChange={e => setFieldValue('date', e)}
                              value={moment(values.date).tz(timezone)}
                              timeFormat={true}
                              locale={getLocaleName(user.language)}
                            />
                          )}
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('Customer')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.customerName}</div>
                          : (
                            <Dropdown onChange={(e, data) => this.onCustomerChange(data.value, values, setValues)}
                              value={values.customerId || ''}
                              fluid search selection options={customerOptions} />
                          )}
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('Account Type')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.accountType || AccountType.Open}</div>
                          : (
                            <FormControl
                              name="accountType"
                              componentClass="select"
                              value={values.accountType || AccountType.Open}
                              disabled={isPreview}
                              onChange={handleChange}
                              bsSize="sm"
                            >
                              {accountType.map((option, i) => (
                                <option key={i} value={option.value}>
                                  {option.text}
                                </option>
                              ))}
                            </FormControl>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className={'no-borders'}>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('Truck')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.truckCode}</div>
                          : (
                            <Dropdown onChange={(e, data) => this.onTruckChange(data.value, values, setValues)}
                              value={values.truckId || ''}
                              disabled={isPreview}
                              fluid search selection options={truckOptions}/>
                          )}
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('Driver')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.driverCode} {values.driverName}</div>
                          : (
                            <Dropdown onChange={(e, data) => this.onDriverChange(data.value, values, setValues)}
                              value={values.driverId || ''}
                              disabled={isPreview}
                              fluid search selection options={driverOptions} />
                          )}
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('Usage')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.usage}</div>
                          : (
                            <Field name="usage"
                              value={values.usage || ''}
                              disabled={isPreview}
                              onChange={handleChange}
                              className="form-control input-sm" />
                          )}
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup>
                        <ControlLabel>{t('PO')}</ControlLabel>
                        {isPreview
                          ?
                          <div className="form-data-value">{values.purchaseOrderNumber}</div>
                          : (
                            <Field name="purchaseOrderNumber"
                              value={values.purchaseOrderNumber || ''}
                              disabled={isPreview}
                              onChange={handleChange}
                              className="form-control input-sm" />
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className={'no-borders'}>
                    <Col sm={6}>
                      <FormGroup>
                        <ControlLabel>{t('Sold To')}</ControlLabel>
                        <Field name="customerAddress"
                          component="textarea"
                          value={values.customerAddress || ''}
                          disabled={isPreview}
                          onChange={handleChange}
                          className="form-control input-sm" />
                      </FormGroup>
                    </Col>
                    <Col sm={6}>
                      <FormGroup>
                        <ControlLabel>{t('Ship To')}</ControlLabel>
                        <Field name="shipToAddress"
                          component="textarea"
                          value={values.shipToAddress || ''}
                          disabled={isPreview}
                          onChange={handleChange}
                          className="form-control input-sm" />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className={'no-borders'}>
                    <Col sm={3}>
                      <FormGroup>
                        <Checkbox
                          inline
                          className="mt-checkbox mt-checkbox-outline"
                          name={'printWithoutWeights'}
                          value={values.printWithoutWeights}
                          checked={!!values.printWithoutWeights}
                          onChange={e => {
                            setFieldValue('printWithoutWeights', !!e.target.checked);
                            if (!!e.target.checked && !!values.printWithWeights) {
                              setFieldValue('printWithWeights', false);
                            }
                          }}
                        >
                          <span />
                          {t('Print Ticket')}
                        </Checkbox>
                        <Checkbox
                          inline
                          className="mt-checkbox mt-checkbox-outline"
                          name={'printWithWeights'}
                          value={values.printWithWeights}
                          checked={!!values.printWithWeights}
                          onChange={e => {
                            setFieldValue('printWithWeights', !!e.target.checked);
                            if (!!e.target.checked && !!values.printWithoutWeights) {
                              setFieldValue('printWithoutWeights', false);
                            }
                          }}
                        >
                          <span />
                          {t('Print Ticket With Weights')}
                        </Checkbox>
                      </FormGroup>
                    </Col>
                    <Col sm={3}>
                      <FormGroup>
                        <Checkbox
                          inline
                          className="mt-checkbox mt-checkbox-outline"
                          name={'printOnlyWeights'}
                          value={values.printOnlyWeights}
                          checked={!!values.printOnlyWeights}
                          disabled={isPreview}
                          onChange={handleChange}
                        >
                          <span />
                          {t('Print Weights')}
                        </Checkbox>
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <FieldArray
                    name="products"
                    render={arrayHelpers => (
                      <div className="table-form">
                        <Panel>
                          <Panel.Body>
                            {values.products && values.products.length > 0 ? (
                              values.products.map((product, index) => (
                                <Row key={index}>
                                  <Col sm={4} xs={5}>
                                    <FormGroup className={cx({'has-error':
                                        errors.products && errors.products[index] && errors.products[index].itemId
                                        && touched.products && touched.products[index] && touched.products[index].itemId})}>
                                      <ControlLabel>{ product.isMix ? t('Mix/Product') : t('Product') }</ControlLabel>
                                      {!product.itemId && orderId
                                        ?
                                        <div className="form-data-value">{product.itemCode} {product.itemDescription}</div>
                                        : (
                                          <Dropdown onChange={(e, data) => this.onProductChange(data.value, product, index, values, setValues)}
                                            value={values.products[index].itemId || ''}
                                            disabled={isPreview}
                                            fluid search selection options={product.isMix ? mixOptions : extraOptions}/>
                                        )}
                                      <ErrorMessage component="span" name={`products.${index}.itemId`} className="help-block" />
                                    </FormGroup>
                                  </Col>
                                  <Col sm={2} xs={2}>
                                    <FormGroup className={cx({'has-error':
                                        errors.products && errors.products[index] && errors.products[index].quantity
                                        && touched.products && touched.products[index] && touched.products[index].quantity})}>
                                      <ControlLabel>{t('Qty/Drop Size')}</ControlLabel>
                                      <FormControl
                                        name={`products.${index}.quantity`}
                                        type="number"
                                        value={values.products[index].quantity || 0}
                                        disabled={isPreview}
                                        onChange={e => {
                                          setFieldValue(`products[${index}].quantity`, e.target.value);
                                          if (product.isMix) {
                                            setFieldValue('orderMixQuantity', e.target.value);
                                          }
                                        }}
                                        bsSize="sm"
                                      />
                                      <ErrorMessage component="span" name={`products[${index}].quantity`} className="help-block" />
                                    </FormGroup>
                                  </Col>
                                  <Col sm={1} xs={2}>
                                    <FormGroup className={cx({'has-error':
                                        errors.products && errors.products[index] && errors.products[index].unitPrice
                                        && touched.products && touched.products[index] && touched.products[index].unitPrice})}>
                                      <ControlLabel>{t('Price')}</ControlLabel>
                                      <FormControl
                                        name={`products.${index}.unitPrice`}
                                        type="number"
                                        value={values.products[index].unitPrice || 0}
                                        disabled={isPreview}
                                        onChange={e => {
                                          setFieldValue(`products[${index}].unitPrice`, e.target.value);
                                        }}
                                        bsSize="sm"
                                      />
                                      <ErrorMessage component="span" name={`products[${index}].unitPrice`} className="help-block" />
                                    </FormGroup>
                                  </Col>
                                  <Col sm={1} xs={2}>
                                    <FormGroup>
                                      <ControlLabel>{t('Amount')}</ControlLabel>
                                      <div className="form-data-value">{values.products[index].unitPrice * values.products[index].quantity || 0}</div>
                                    </FormGroup>
                                  </Col>
                                  <Col sm={1} xsHidden={true}>
                                    <FormGroup>
                                      <ControlLabel>{t('Unit')}</ControlLabel>
                                      <div className="form-data-value">{values.products[index].unitSymbol}</div>
                                    </FormGroup>
                                  </Col>
                                  <Col sm={1} xsHidden={true}>
                                    <FormGroup>
                                      <ControlLabel>{t('Taxable')}</ControlLabel>
                                      <div className="form-data-value">{values.products[index].isTaxable ? 'Y' : 'N'}</div>
                                    </FormGroup>
                                  </Col>
                                  {product.isMix && (
                                    <Col sm={1} xsHidden={true}>
                                      <FormGroup>
                                        <ControlLabel>{t('Slump')}</ControlLabel>
                                        <div className="form-data-value">{values.mixSlump || 0}</div>
                                      </FormGroup>
                                    </Col>
                                  )}
                                  { !values.products[index].isMix && !isPreview && (
                                    <Col sm={2}>
                                      <a className="btn btn-sm btn-default  btn-action pull-right"
                                        title="Remove"
                                        onClick={() => arrayHelpers.remove(index)}>
                                        <i className="fa fa-remove"/>
                                      </a>
                                    </Col>
                                  )}
                                </Row>

                              ))
                            ) : ''}

                            {(isAdd || isEdit) && (
                              <Row>
                                <Col sm={2}>
                                  <a className="btn btn-sm btn-default"
                                    title="Add product" onClick={() => arrayHelpers.push({
                                      isMix: false,
                                      quantity: 0,
                                      itemId: '',
                                    })}>
                                    <i className="fa fa-plus"/>
                                  </a>
                                </Col>
                              </Row>
                            )}
                          </Panel.Body>
                        </Panel>
                      </div>
                    )}
                  />

                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <Panel>
                    <Panel.Body>
                      <Row>
                        <Col sm={2}>
                          <FormGroup>
                            <ControlLabel>{t('Qty Sold')}</ControlLabel>
                            <FormControl
                              name="orderMixQuantity"
                              type="number"
                              value={values.orderMixQuantity || 0}
                              disabled={isPreview}
                              onChange={handleChange}
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm={2}>
                          <FormGroup>
                            <ControlLabel>{t('Qty Delivered')}</ControlLabel>
                            <FormControl
                              name="orderDeliveredMixQuantity"
                              type="number"
                              value={values.orderDeliveredMixQuantity || 0}
                              disabled={isPreview}
                              onChange={handleChange}
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm={2}>
                          <FormGroup>
                            <ControlLabel>{t('Sub Total $')}</ControlLabel>
                            <FormControl
                              name="subTotal"
                              type="number"
                              disabled={true}
                              value={values.totalQuantityPrice || this.getSubTotal(values) || 0}
                              onChange={handleChange}
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm={2}>
                          <FormGroup>
                            <ControlLabel>{t('Tax')} %</ControlLabel>
                            <FormControl
                              name="taxRate"
                              type="number"
                              value={values.taxRate || ''}
                              disabled={isPreview}
                              onChange={handleChange}
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm={2}>
                          <FormGroup>
                            <ControlLabel>{t('Tax')} $</ControlLabel>
                            <FormControl
                              name="taxAmount"
                              type="number"
                              disabled={true}
                              value={values.totalQuantityTax || this.getTax(values) || 0}
                              onChange={handleChange}
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm={2}>
                          <FormGroup>
                            <ControlLabel>{t('Total')} $</ControlLabel>
                            <FormControl
                              name="total"
                              type="number"
                              disabled={true}
                              value={toNumber(values.totalQuantityPrice + values.totalQuantityTax, 2) || (values.subTotal + values.taxAmount) || 0}
                              onChange={handleChange}
                              bsSize="sm"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </Panel.Body>
                  </Panel>
                </Col>
                {!isPreview && (
                  <Col sm={12}>
                    <Row>
                      <Col sm={12} className="text-right">
                        {!!this.state.error && <Alert bsStyle="danger">{this.state.error}</Alert>}
                        <Button onClick={() => this.onResetForm(resetForm)}>Cancel</Button>
                      &nbsp;&nbsp;
                        <LoadingButton
                          bsStyle="primary"
                          type="submit"
                          label={'Save'}
                          loadingLabel="Saving"
                          onClick={handleSubmit}
                          isLoading={isSubmitting}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>

            </Form>
          )}
        </Formik>
      </SideBar>
    );
  }
}

export default withTranslation()(inject('scheduleStore', 'sidebarStore', 'settingsStore', 'mixStore', 'batchStore', 'newAuthStore')(observer(OrderForm)));
